import React from "react";
import { Map, Rocket, GitBranch, GraduationCap } from "lucide-react";

const offerings = [
  {
    icon: Map,
    n: "01",
    title: "New Market Entry & Territory Expansion",
    body: "Want to sell in a new city, state or segment? We map the market, identify the right buyers and channel partners, open the doors, and hand you a working territory — built in your company's name.",
  },
  {
    icon: Rocket,
    n: "02",
    title: "Demand Generation & BD as a Service",
    body: "A senior business development team — without hiring one. Structured outbound every month, key-account pursuit, partnerships and channels, with a one-page board the owner reviews monthly.",
  },
  {
    icon: GitBranch,
    n: "03",
    title: "Sales Process & Systems",
    body: "We map how orders actually come today, install qualification discipline and follow-up rhythm, and give the owner a pipeline he can read in two minutes a day — so enquiries stop dying in notebooks and WhatsApp.",
  },
  {
    icon: GraduationCap,
    n: "04",
    title: "Sales Training & Enablement",
    body: "Practical sprints for the people who quote and follow up — on your own products and live deals. Value before price, objection handling, closing. Scored drills and role-plays, not lectures.",
  },
];

const pilots = [
  ["P1", "Sales process audit", "See gaps clearly"],
  ["P2", "Structure & owner visibility", "Simple operating rhythm"],
  ["P3", "Qualification & follow-up", "Fewer lost enquiries"],
  ["P4", "Enquiry → order workflow", "Full pipeline clarity"],
];

export default function GrowthOfferings() {
  return (
    <section
      id="growth"
      data-testid="growth-offerings"
      className="px-6 md:px-12 lg:px-24 py-24 md:py-32 border-t border-sebyx-border"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="block w-8 h-px bg-sebyx-accent" />
        <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
          [02] What We Do
        </span>
      </div>
      <h2
        data-testid="growth-offerings-headline"
        className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-sebyx-text leading-[1.05] max-w-3xl"
      >
        Four ways we drive{" "}
        <span className="text-sebyx-accent italic font-light">growth</span>.
      </h2>
      <p className="mt-5 font-body text-base md:text-lg text-sebyx-muted max-w-2xl leading-relaxed">
        We do not presume to teach you your business. We put what you have
        already built — relationships, knowledge, hard-won customers — into a
        system your company can run, measure and scale.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
        {offerings.map((o) => (
          <div
            key={o.n}
            data-testid={`growth-offering-${o.n}`}
            className="group border border-sebyx-border bg-sebyx-surface p-7 md:p-9 hover:border-sebyx-accent/60 transition-colors"
          >
            <div className="flex items-center justify-between">
              <o.icon className="text-sebyx-accent" size={26} />
              <span className="font-mono text-xs text-sebyx-muted">{o.n}</span>
            </div>
            <h3 className="mt-5 font-display text-lg md:text-xl font-semibold text-sebyx-text leading-snug">
              {o.title}
            </h3>
            <p className="mt-3 font-body text-sm md:text-base text-sebyx-muted leading-relaxed">
              {o.body}
            </p>
          </div>
        ))}
      </div>

      <div
        data-testid="growth-pilot-path"
        className="mt-8 border border-sebyx-border bg-sebyx-surface p-6 md:p-8"
      >
        <div className="font-mono text-[11px] tracking-[0.25em] uppercase text-sebyx-accent mb-5">
          Start small — the pilot path
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {pilots.map(([p, t, d]) => (
            <div key={p} data-testid={`pilot-${p}`}>
              <span className="font-mono text-sm font-semibold text-sebyx-accent">
                {p}
              </span>
              <div className="font-display text-sm font-semibold text-sebyx-text mt-1">
                {t}
              </div>
              <div className="font-body text-xs text-sebyx-muted mt-1">{d}</div>
            </div>
          ))}
        </div>
        <p className="mt-5 font-body text-xs md:text-sm text-sebyx-muted">
          Most companies begin with a short, fixed-scope pilot — so impact is
          visible before any larger engagement.
        </p>
      </div>
    </section>
  );
}
